/**
 * contract-sign-page.tsx — Trang KÝ HỢP ĐỒNG công khai `/ky/:token` (khách, KHÔNG đăng nhập, ngoài AppLayout).
 *
 * Luồng: xem nội dung → nhập họ tên → vẽ chữ ký → "Gửi mã xác thực" (OTP 6 số về email khách) → nhập mã → tick đồng ý → Ký.
 * Quyền = nắm link (token 256-bit) + mã OTP email. Link sai / hết hạn / đã huỷ hiện CÙNG một thông báo.
 * Backend: modules/platform/contract/contract-public-routes.ts. Trang đặt `noindex` + không gửi Referer.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle2, Loader2, Mail, ShieldCheck, XCircle } from 'lucide-react'
import { BrandLogo } from '@/components/shared/brand-logo'
import { Loading } from '@/components/shared/feedback'
import { SignaturePad, type SignaturePadHandle } from '@/components/shared/signature-pad'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Textarea } from '@/components/ui/misc'
import {
  PublicApiError, declinePublicContract, fetchPublicContract, requestContractOtp, signPublicContract,
} from '@/hooks/use-contracts'
import { cooldownLeft, fmtDateTime, normalizeOtp, signBlocker, type PublicContract } from '@/pages/platform/contract/contract-helpers'

type Phase = 'loading' | 'invalid' | 'view' | 'signed' | 'declined'

export function ContractSignPage() {
  const { token = '' } = useParams<{ token: string }>()
  const [phase, setPhase] = useState<Phase>('loading')
  const [c, setC] = useState<PublicContract | null>(null)
  const [signedAt, setSignedAt] = useState<string | null>(null)
  const [emailSent, setEmailSent] = useState(true)

  useEffect(() => {
    document.title = 'Ký hợp đồng'
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex,nofollow'
    document.head.appendChild(meta)
    return () => { meta.remove() }
  }, [])

  useEffect(() => {
    let alive = true
    fetchPublicContract(token)
      .then((r) => {
        if (!alive) return
        setC(r)
        setPhase(r.status === 'signed' ? 'signed' : r.status === 'declined' ? 'declined' : r.status === 'sent' ? 'view' : 'invalid')
        if (r.signedAt) setSignedAt(r.signedAt)
      })
      .catch(() => { if (alive) setPhase('invalid') })
    return () => { alive = false }
  }, [token])

  return (
    <div className="min-h-screen bg-shell px-4 py-6">
      <div className="mx-auto max-w-3xl space-y-4">
        <BrandLogo variant="header" />
        {phase === 'loading' && <Loading label="Đang tải hợp đồng..." />}
        {phase === 'invalid' && (
          <Card className="space-y-2 p-6 text-center">
            <XCircle className="mx-auto h-10 w-10 text-muted-foreground" />
            <h1 className="text-lg font-semibold">Liên kết không hợp lệ hoặc đã hết hạn</h1>
            <p className="text-sm text-muted-foreground">Vui lòng liên hệ bên gửi hợp đồng để nhận liên kết mới.</p>
          </Card>
        )}
        {phase === 'signed' && c && (
          <Card className="space-y-2 p-6 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
            <h1 className="text-lg font-semibold">Hợp đồng {c.number} đã được ký</h1>
            <p className="text-sm text-muted-foreground">
              Ký lúc {fmtDateTime(signedAt)}.{' '}
              {emailSent ? 'Bản PDF đã ký được gửi về email của anh/chị.' : 'Bên gửi sẽ chuyển bản PDF đã ký cho anh/chị.'}
            </p>
          </Card>
        )}
        {phase === 'declined' && c && (
          <Card className="space-y-2 p-6 text-center">
            <XCircle className="mx-auto h-10 w-10 text-muted-foreground" />
            <h1 className="text-lg font-semibold">Anh/chị đã từ chối hợp đồng {c.number}</h1>
            <p className="text-sm text-muted-foreground">Nếu cần thay đổi nội dung, hãy liên hệ bên gửi để được lập hợp đồng mới.</p>
          </Card>
        )}
        {phase === 'view' && c && (
          <Reader
            token={token}
            c={c}
            onSigned={(at, sent) => { setSignedAt(at); setEmailSent(sent); setPhase('signed') }}
            onDeclined={() => setPhase('declined')}
            onGone={() => setPhase('invalid')}
          />
        )}
      </div>
    </div>
  )
}

function Reader({ token, c, onSigned, onDeclined, onGone }: {
  token: string
  c: PublicContract
  onSigned: (signedAt: string, emailSent: boolean) => void
  onDeclined: () => void
  onGone: () => void
}) {
  const pad = useRef<SignaturePadHandle>(null)
  const [name, setName] = useState(c.customer.fullName)
  const [hasStroke, setHasStroke] = useState(false)
  const [otp, setOtp] = useState('')
  const [agree, setAgree] = useState(false)
  const [otpSentAt, setOtpSentAt] = useState<number | null>(null)
  const [otpTo, setOtpTo] = useState('')
  const [now, setNow] = useState(Date.now())
  const [sending, setSending] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [declining, setDeclining] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (otpSentAt === null) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [otpSentAt])

  const wait = cooldownLeft(otpSentAt, now)

  /** Lỗi từ server → thông báo; link hết hiệu lực giữa chừng → về màn "không hợp lệ". */
  const handle = useCallback((e: unknown) => {
    if (e instanceof PublicApiError && e.code === 'LINK_INVALID') { onGone(); return }
    setError(e instanceof Error ? e.message : 'Có lỗi xảy ra — vui lòng thử lại')
  }, [onGone])

  const sendOtp = async () => {
    setError(null); setInfo(null); setSending(true)
    try {
      const r = await requestContractOtp(token)
      setOtpSentAt(Date.now()); setNow(Date.now()); setOtpTo(r.to)
      setInfo(`Đã gửi mã 6 số tới ${r.to}. Mã có hiệu lực ${r.ttlMinutes} phút.`)
    } catch (e) { handle(e) } finally { setSending(false) }
  }

  const submit = async () => {
    setError(null); setInfo(null)
    const sig = pad.current?.toDataUrl() ?? null
    const block = signBlocker({ name, otp, hasStroke: !!sig, agree, otpSent: otpSentAt !== null })
    if (block || !sig) { setError(block ?? 'Hãy vẽ chữ ký vào khung'); return }
    setSubmitting(true)
    try {
      const r = await signPublicContract(token, { otp, signerName: name.trim(), signature: sig, agree })
      onSigned(r.signedAt, r.emailSent)
    } catch (e) { handle(e) } finally { setSubmitting(false) }
  }

  const decline = async () => {
    setError(null); setSubmitting(true)
    try { await declinePublicContract(token, reason.trim()); onDeclined() } catch (e) { handle(e) } finally { setSubmitting(false) }
  }

  return (
    <>
      <Card className="space-y-3 p-5">
        <div>
          <h1 className="text-xl font-bold">HỢP ĐỒNG DỊCH VỤ</h1>
          <p className="text-sm text-muted-foreground">Số {c.number} · {c.seller.companyName}</p>
          {c.expiresAt && <p className="text-xs text-muted-foreground">Hạn ký: {fmtDateTime(c.expiresAt)}</p>}
        </div>
        <div className="max-h-[55vh] space-y-3 overflow-y-auto rounded-md border bg-card p-4 text-sm leading-relaxed" tabIndex={0} aria-label="Nội dung hợp đồng">
          {c.clauses.map((cl, i) => (
            <section key={i}>
              <h2 className="font-semibold">{cl.title}</h2>
              <p className="whitespace-pre-line">{cl.text}</p>
            </section>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Bên A ({c.seller.companyName}) đã ký sẵn{c.seller.signerName ? ` — đại diện ${c.seller.signerName}${c.seller.signerTitle ? `, ${c.seller.signerTitle}` : ''}` : ''}.
        </p>
      </Card>

      <Card className="space-y-4 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary"><ShieldCheck className="h-4 w-4" /> Ký xác nhận</h2>
        <div className="space-y-1">
          <Label htmlFor="sg-name">Họ và tên người ký</Label>
          <Input id="sg-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoComplete="name" />
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <Label>Chữ ký của anh/chị (vẽ bằng chuột hoặc ngón tay)</Label>
            <Button type="button" size="sm" variant="ghost" onClick={() => pad.current?.clear()}>Xoá, vẽ lại</Button>
          </div>
          <SignaturePad ref={pad} onInkChange={setHasStroke} />
        </div>

        <div className="space-y-2 rounded-md border bg-muted/30 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={sending || wait > 0} onClick={sendOtp}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {otpSentAt === null ? 'Gửi mã xác thực về email' : wait > 0 ? `Gửi lại mã sau ${wait}s` : 'Gửi lại mã'}
            </Button>
            <span className="text-xs text-muted-foreground">Mã gửi tới {otpTo || c.customer.emailMasked}</span>
          </div>
          <div className="space-y-1">
            <Label htmlFor="sg-otp">Mã xác thực (6 số)</Label>
            <Input
              id="sg-otp" inputMode="numeric" autoComplete="one-time-code" placeholder="••••••" className="max-w-[10rem] font-mono tracking-[0.3em]"
              value={otp} onChange={(e) => setOtp(normalizeOtp(e.target.value))}
            />
          </div>
        </div>

        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={agree} onCheckedChange={(v) => setAgree(v === true)} className="mt-0.5" />
          <span>Tôi đã đọc và đồng ý với toàn bộ nội dung hợp đồng này; chữ ký vẽ tay cùng mã xác thực email có giá trị như chữ ký của tôi.</span>
        </label>

        {error && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>}
        {info && <p className="rounded-md bg-primary/10 px-3 py-2 text-sm text-primary">{info}</p>}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => setDeclining((v) => !v)}>Tôi không đồng ý / từ chối</Button>
          <Button type="button" disabled={submitting} onClick={submit}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Ký hợp đồng
          </Button>
        </div>
        {!hasStroke && <p className="text-xs text-muted-foreground">Chưa có chữ ký — hãy vẽ vào khung phía trên.</p>}

        {declining && (
          <div className="space-y-2 rounded-md border p-3">
            <Label htmlFor="sg-reason">Lý do từ chối (không bắt buộc)</Label>
            <Textarea id="sg-reason" className="min-h-[60px]" maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button type="button" variant="destructive" size="sm" disabled={submitting} onClick={decline}>Xác nhận từ chối</Button>
          </div>
        )}
      </Card>
    </>
  )
}
